import React from 'react';
import { Sparkles, Check, Loader2, Circle } from 'lucide-react';
import { cn } from 'App/shadcn/lib/utils';
import T from 'Components/Helpers/translate';

/**
 * Shared step-render primitives for the Playbook compile drawer.
 *
 * Extracted from WorkspacePlaybookDraft so the compiled-step read-out (args,
 * prompts, filters, the tool-call header) lives in one place. Re-imported by
 * WorkspacePlaybookDraft.
 */

// "suggestReplies" -> "Suggest replies". Fallback when there's no summary/name.
export const humanizeIdentifier = (raw: string): string => {
	const spaced = raw
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/[._-]+/g, ' ')
		.trim();
	if (!spaced) return raw;
	return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

export const MONO: React.CSSProperties = {
	fontFamily: 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
	fontSize: 12,
	wordBreak: 'break-word'
};

export const DETAIL_HEADING: React.CSSProperties = {
	fontSize: 10.5,
	fontWeight: 600,
	letterSpacing: '0.04em',
	textTransform: 'uppercase',
	color: 'var(--ws-ink-4, #8a94a6)',
	marginBottom: 6
};

// Only these shared read/report args get a localized label. Any other tool arg
// is humanized from its key — critically, T() returns the key itself when a tag
// is missing, so wrapping an arbitrary key in T() would surface the raw
// "assistant.playbook.arg.foo" string. Humanize those instead.
const KNOWN_ARG_KEYS = new Set(['fields', 'filters', 'limit', 'offset', 'replies', 'sort']);
const argLabel = (key: string): string =>
	KNOWN_ARG_KEYS.has(key) ? T(`assistant.playbook.arg.${key}`) : humanizeIdentifier(key);

// Render a leaf arg value. Scalars inline; a list of scalars as a comma list;
// anything nested falls back to compact JSON (rare for read args).
export const formatArgValue = (value: unknown): string => {
	if (value === null || value === undefined || value === '') return '—';
	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}
	if (Array.isArray(value)) {
		if (value.every(v => typeof v === 'string' || typeof v === 'number')) return value.join(', ');
		return JSON.stringify(value);
	}
	return JSON.stringify(value);
};

// A compiled arg can carry `{{…}}` template placeholders filled in per run:
// `{{step_2_llm.field}}` is AI-written, `{{steps.x}}` / `{{trigger.y}}` carry a
// value forward from an earlier step or the trigger. Rendered as inline chips
// instead of raw braces so the user sees "an AI value goes here", not jargon.
const BINDING_RE = /\{\{\s*([^}]+?)\s*\}\}/g;
const isAiBinding = (expr: string): boolean => /llm/i.test(expr);

/**
 * Step id → the label that step is showing on screen right now.
 *
 * A binding like `{{step_1}}` used to render as the chip "Step 1", which named
 * nothing the reader could see: the run trace lists steps by name with no
 * numbers, and the drawer numbers them but titles them ("Find new leads since
 * cutoff"), so the number in the id and the number on the row are only ever
 * the same by luck — a promoted LLM step shifts one and not the other. Each
 * surface fills this with the labels it is actually rendering, so a reference
 * points at something findable.
 */
export const StepLabelContext = React.createContext<Record<string, string>>({});

const bindingLabel = (expr: string, stepLabels: Record<string, string>): string => {
	// `{{step_1}}`, `{{steps.step_1}}`, `{{step_2_llm.field}}` — the step id is
	// the first segment that names a step we know about.
	for (const part of expr.split('.')) {
		const named = stepLabels[part];
		if (named) return named;
	}
	return humanizeIdentifier(expr.split('.').pop() || expr);
};

const BINDING_PILL_BASE: React.CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: 3,
	fontSize: 11,
	fontWeight: 600,
	padding: '0 6px',
	borderRadius: 9,
	whiteSpace: 'nowrap'
};

/** Inline chip standing in for a `{{…}}` placeholder in a rendered value. */
export const BindingPill = ({ expr }: { expr: string }) => {
	const stepLabels = React.useContext(StepLabelContext);
	const ai = isAiBinding(expr);
	const name = bindingLabel(expr, stepLabels);
	const title = ai
		? T('assistant.playbook.aiValueTitle', { name }) || `AI-generated value: ${name}`
		: T('assistant.playbook.linkedValueTitle', { name }) || `Carried over from an earlier step: ${name}`;
	return (
		<span
			title={title}
			style={{
				...BINDING_PILL_BASE,
				color: ai ? 'var(--ws-info, #4a6cf7)' : 'var(--ws-ink-3, #5b6472)',
				background: ai ? 'var(--ws-info-bg, rgba(74, 108, 247, 0.1))' : 'var(--ws-bg-3, #e6e8eb)'
			}}
		>
			{ai && <Sparkles style={{ width: 10, height: 10 }} />}
			{name}
		</span>
	);
};

/** Render a value string, turning any `{{…}}` placeholders into inline chips. */
export const ValueText = ({ text }: { text: string }) => {
	const nodes: React.ReactNode[] = [];
	let last = 0;
	let key = 0;
	BINDING_RE.lastIndex = 0;
	let match: RegExpExecArray | null = BINDING_RE.exec(text);
	while (match !== null) {
		if (match.index > last) nodes.push(text.slice(last, match.index));
		nodes.push(<BindingPill key={key++} expr={match[1]} />);
		last = match.index + match[0].length;
		match = BINDING_RE.exec(text);
	}
	if (last < text.length) nodes.push(text.slice(last));
	return <>{nodes.length ? nodes : text}</>;
};

type FilterLike = { field?: unknown; comparison?: unknown; value?: unknown };
// A CRM read/report `filters` array: [{ field, comparison, value }, …]. Rendered
// as readable "field comparison value" rows instead of raw JSON.
export const isFilterArray = (value: unknown): value is FilterLike[] =>
	Array.isArray(value) &&
	value.length > 0 &&
	value.every(v => v !== null && typeof v === 'object' && 'field' in (v as object));

// An array of sentence-like strings (e.g. `replies`) — render one per line
// rather than comma-joining, which would read as a single run-on value.
const isPhraseList = (value: unknown): value is string[] =>
	Array.isArray(value) &&
	value.length > 1 &&
	value.every(v => typeof v === 'string') &&
	value.some(v => (v as string).includes(' '));

/** One "field comparison value" row inside a rendered filter list. */
export const FilterRow = ({ filter }: { filter: FilterLike }) => (
	<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'baseline', ...MONO }}>
		<span style={{ fontWeight: 600 }}>{humanizeIdentifier(String(filter.field ?? ''))}</span>
		<span style={{ color: 'var(--ws-ink-4, #8a94a6)' }}>{String(filter.comparison ?? '')}</span>
		<span>
			<ValueText text={formatArgValue(filter.value)} />
		</span>
	</div>
);

/** A single labelled arg (e.g. Filters, Limit, Sort) inside the drilldown. */
export const ArgRow = ({ argKey, value }: { argKey: string; value: unknown }) => (
	<div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
		<span style={{ flexShrink: 0, minWidth: 64, fontSize: 12, color: 'var(--ws-ink-4, #8a94a6)' }}>
			{argLabel(argKey)}
		</span>
		<div style={{ minWidth: 0, flex: 1 }}>
			{isFilterArray(value) ? (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					{value.map((f, i) => (
						<FilterRow key={i} filter={f} />
					))}
				</div>
			) : isPhraseList(value) ? (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					{value.map((v, i) => (
						<span key={i} style={MONO}>
							<ValueText text={v} />
						</span>
					))}
				</div>
			) : (
				<span style={MONO}>
					<ValueText text={formatArgValue(value)} />
				</span>
			)}
		</div>
	</div>
);

/** A dimmed "AI instruction" block showing the prompt behind an AI-written value. */
export const PromptBlock = ({ prompt }: { prompt: unknown }) => (
	<div>
		<div style={DETAIL_HEADING}>{T('assistant.playbook.detailsPrompt') || 'AI instruction'}</div>
		<span style={{ ...MONO, whiteSpace: 'pre-wrap' }}>
			<ValueText text={formatArgValue(prompt)} />
		</span>
	</div>
);

/**
 * Capability line as a function-call signature: `tool(entity, …)`. The tool id
 * is the function, the entity is the bold first arg (what it acts on), and `…`
 * flags that parameters follow below.
 */
export const ToolCallHeader = ({
	tool,
	entity,
	hasParams
}: {
	tool: string;
	entity: string | null;
	hasParams: boolean;
}) => (
	<div style={{ ...MONO, fontSize: 13 }}>
		<span>{tool}</span>
		<span style={{ opacity: 0.5 }}>{'('}</span>
		{entity && <span style={{ fontWeight: 700 }}>{entity}</span>}
		{hasParams && <span style={{ opacity: 0.5 }}>{entity ? ', …' : '…'}</span>}
		<span style={{ opacity: 0.5 }}>{')'}</span>
	</div>
);

// One step of a Playbook/workflow run. Seeded from `run_plan` (tool + template
// args, or llm prompt); `args` becomes resolved values on `tool_use`, and
// `resultPreview` fills on `tool_result`. Persisted on the finished run's result
// message so the executed steps survive as a collapsed section (WEB-7897).
export type PlaybookRunStep = {
	id: string;
	kind: string;
	status?: 'pending' | 'running' | 'done';
	tool?: string;
	args?: Record<string, unknown>;
	prompt?: unknown;
	resultPreview?: { count?: number; text?: string };
};

/**
 * The run's steps as a list of compile-drawer cards — the tool-call signature,
 * its resolved parameters, and a per-step result count/snippet. Shared by the
 * live in-run checklist and the collapsed "Steps" record on a finished run, so
 * both read identically.
 */
/** The label a run-trace row shows for a step — the same string the header renders. */
const runStepLabel = (step: PlaybookRunStep): string => {
	if (step.kind === 'tool' && step.tool) {
		const args = (step.args && typeof step.args === 'object' ? step.args : {}) as Record<string, unknown>;
		const entity = typeof args.entity === 'string' || typeof args.entity === 'number' ? String(args.entity) : null;
		return entity ? `${step.tool}(${entity})` : step.tool;
	}
	if (step.kind === 'llm') return T('assistant.playbook.stepLlm') || 'Write summary';
	if (step.kind === 'if') return T('assistant.playbook.stepIf') || 'Check condition';
	return T('assistant.playbook.stepForeach') || 'For each item';
};

export const PlaybookStepList = ({ steps }: { steps: PlaybookRunStep[] }) => (
	// Publish what each row is called so a `{{step_1}}` inside a prompt below
	// renders as that row's name instead of an id the list never shows.
	<StepLabelContext.Provider value={Object.fromEntries(steps.map(s => [s.id, runStepLabel(s)]))}>
		<div className="flex flex-col gap-2">
			{steps.map(step => {
				const args = (step.args && typeof step.args === 'object' ? step.args : {}) as Record<string, unknown>;
				const entity =
					typeof args.entity === 'string' || typeof args.entity === 'number' ? String(args.entity) : null;
				const paramEntries = Object.entries(args).filter(([k]) => k !== 'entity');
				const kindLabel =
					step.kind === 'llm'
						? T('assistant.playbook.stepLlm') || 'Write summary'
						: step.kind === 'if'
						? T('assistant.playbook.stepIf') || 'Check condition'
						: T('assistant.playbook.stepForeach') || 'For each item';
				const preview = step.resultPreview;
				return (
					<div
						key={step.id}
						className={cn(
							'flex flex-col gap-1.5 rounded-lg p-2.5',
							step.status === 'pending' && 'opacity-60'
						)}
						style={{ background: 'var(--ws-bg-2, #f6f7f9)' }}
					>
						<div className="flex items-center gap-2">
							{step.status === 'done' ? (
								<Check className="h-3.5 w-3.5 text-green-6 shrink-0" />
							) : step.status === 'running' ? (
								<Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
							) : step.status === 'pending' ? (
								<Circle className="h-3.5 w-3.5 shrink-0 opacity-40" />
							) : null}
							{step.kind === 'tool' && step.tool ? (
								<ToolCallHeader tool={step.tool} entity={entity} hasParams={paramEntries.length > 0} />
							) : (
								<span style={{ ...MONO, fontSize: 13 }}>{kindLabel}</span>
							)}
						</div>
						{step.kind === 'tool' && paramEntries.length > 0 && (
							<div className="flex flex-col gap-1 pl-6">
								{paramEntries.map(([k, v]) => (
									<ArgRow key={k} argKey={k} value={v} />
								))}
							</div>
						)}
						{step.kind === 'llm' && step.prompt != null && (
							<div className="pl-6">
								<PromptBlock prompt={step.prompt} />
							</div>
						)}
						{preview && (
							<div className="pl-6 text-body-sm text-grey-9" style={{ minWidth: 0 }}>
								{typeof preview.count === 'number' ? (
									`${preview.count} ${
										preview.count === 1
											? T('assistant.playbook.resultSingular') || 'result'
											: T('assistant.playbook.resultPlural') || 'results'
									}`
								) : (
									<span style={{ wordBreak: 'break-word' }}>{preview.text}</span>
								)}
							</div>
						)}
					</div>
				);
			})}
		</div>
	</StepLabelContext.Provider>
);
